// API utility functions
import { API_BASE_URL } from "./config";

if (!API_BASE_URL) {
  // Helpful console message for developers when the base URL is missing.
  // This often means the .env file wasn't present when the dev server was started
  // or the variable wasn't set before building the production bundle.
  console.warn(
    "REACT_APP_API_BASE_URL is not defined. Set it in a .env file or provide window.__REACT_APP_API_BASE_URL."
  );
} else {
  // console.log("API_BASE_URL:", API_BASE_URL);
}

// Generic API call function
export const apiCall = async (endpoint, options = {}) => {
  if (!API_BASE_URL) {
    // Fail fast to avoid calling the dev server's index.html and getting HTML back.
    throw new Error(
      "REACT_APP_API_BASE_URL is not set. Set REACT_APP_API_BASE_URL in your .env or provide window.__REACT_APP_API_BASE_URL."
    );
  }

  const url = `${API_BASE_URL}${endpoint}`;

  const defaultOptions = {
    headers: {
      "Content-Type": "application/json",
    },
  };

  const config = {
    ...defaultOptions,
    ...options,
    headers: {
      ...defaultOptions.headers,
      ...options.headers,
    },
  };

  try {
    const response = await fetch(url, config);

    if (!response.ok) {
      // Attempt to include any textual response (often HTML error pages) for easier debugging.
      const text = await response.text();
      const snippet = text.slice(0, 1000);
      throw new Error(
        `HTTP error! status: ${response.status}. Response body: ${snippet}`
      );
    }

    // Protect against HTML/error pages returned as text which would break JSON.parse.
    const contentType = response.headers.get("content-type") || "";
    if (!contentType.includes("application/json")) {
      const text = await response.text();
      const snippet = text.slice(0, 2000);
      throw new Error(
        `Expected JSON response but got content-type '${contentType}'. Response body starts with: ${snippet}`
      );
    }

    return await response.json();
  } catch (error) {
    console.error("API call failed:", error);
    throw error;
  }
};

// Specific API functions
export const api = {
  // Post property data to /api/properties
  postProperty: (propertyData) =>
    apiCall("/api/properties", {
      method: "POST",
      body: JSON.stringify(propertyData),
    }),
  // Authentication
  login: (credentials) =>
    apiCall("/api/login", {
      method: "POST",
      body: JSON.stringify(credentials),
    }),

  //   logout: () => apiCall('/api/auth/logout', {
  //     method: 'POST',
  //   }),

  // Survey related
  getSurveys: () => apiCall("/api/surveys"),

  createSurvey: (surveyData) =>
    apiCall("/api/surveys", {
      method: "POST",
      body: JSON.stringify(surveyData),
    }),

  updateSurvey: (id, surveyData) =>
    apiCall(`/api/surveys/${id}`, {
      method: "PUT",
      body: JSON.stringify(surveyData),
    }),

  deleteSurvey: (id) =>
    apiCall(`/api/surveys/${id}`, {
      method: "DELETE",
    }),

  // Get survey by id
  getSurveyById: (id) => {
    let token;
    try {
      if (typeof window !== "undefined" && window.localStorage) {
        token = window.localStorage.getItem("token");
      }
    } catch (e) {
      // ignore
    }
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    return apiCall(`/api/surveys/${id}`, { headers });
  },

  // House/Property related
  getHouses: () => apiCall("/api/houses"),

  getProperties: () => apiCall("/api/properties"),

  createHouse: (houseData) =>
    apiCall("/api/houses", {
      method: "POST",
      body: JSON.stringify(houseData),
    }),

  updateHouse: (id, houseData) =>
    apiCall(`/api/houses/${id}`, {
      method: "PUT",
      body: JSON.stringify(houseData),
    }),

  deleteHouse: (id) =>
    apiCall(`/api/houses/${id}`, {
      method: "DELETE",
    }),

  // Polygon related

  getPolygons: () => apiCall("/api/polygons"),

  updatePolygon: (id, polygonData) =>
    apiCall(`/api/polygons/${id}`, {
      method: "PUT",
      body: JSON.stringify(polygonData),
    }),

  deletePolygon: (id) =>
    apiCall(`/api/properties/${id}`, {
      method: "DELETE",
    }),

  // Dashboard stats
  getDashboardStats: () => {
    // Read token from localStorage if available. Common keys tried: token, authToken, access_token
    let token;
    try {
      if (typeof window !== "undefined" && window.localStorage) {
        token = window.localStorage.getItem("token");
      }
    } catch (e) {
      // ignore localStorage access errors (e.g., privacy modes)
    }
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    return apiCall("/api/dashboard/summary", { headers });
  },
  // Users (portal)
  // Returns list of users from backend endpoint /api/portal/users
  // Assumes the backend returns JSON array of user objects.
  getUsers: () => {
    // Read token from localStorage if available. Common keys tried: token, authToken, access_token
    let token;
    try {
      if (typeof window !== "undefined" && window.localStorage) {
        token = window.localStorage.getItem("token");
      }
    } catch (e) {
      // ignore localStorage access errors (e.g., privacy modes)
    }

    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    return apiCall("/api/portal/users", { headers });
  },
  // Create a new portal user. Expects the payload to include name, email, password, password_confirmation, role, is_portal, phone
  createUser: (userData) => {
    let token;
    try {
      if (typeof window !== "undefined" && window.localStorage) {
        token = window.localStorage.getItem("token");
      }
    } catch (e) {
      // ignore
    }

    const headers = token ? { Authorization: `Bearer ${token}` } : {};

    return apiCall("/api/portal/users", {
      method: "POST",
      body: JSON.stringify(userData),
      headers,
    });
  },
  // Update an existing portal user. id may be numeric id or an email if backend identifies by email.
  updateUser: (id, userData) => {
    let token;
    try {
      if (typeof window !== "undefined" && window.localStorage) {
        token = window.localStorage.getItem("token");
      }
    } catch (e) {
      // ignore
    }

    const headers = token ? { Authorization: `Bearer ${token}` } : {};

    return apiCall(`/api/portal/users/${encodeURIComponent(id)}`, {
      method: "PUT",
      body: JSON.stringify(userData),
      headers,
    });
  },
  // Delete an existing portal user by id or identifier (email)
  deleteUser: (id) => {
    let token;
    try {
      if (typeof window !== "undefined" && window.localStorage) {
        token = window.localStorage.getItem("token");
      }
    } catch (e) {
      // ignore
    }

    const headers = token ? { Authorization: `Bearer ${token}` } : {};

    return apiCall(`/api/portal/users/${encodeURIComponent(id)}`, {
      method: "DELETE",
      headers,
    });
  },
};

export default api;
